/**
 * Universal Location Input - Utility Functions
 * Address parsing, mode configuration, and data processing utilities
 */

import { createLogger } from '@/utils/logger/devLogger';
import type { 
  UniversalLocationData, 
  ComponentConfig, 
  PlaceData, 
  AddressComponents 
} from './types';

const universalLocationLogger = createLogger('UniversalLocation');

/**
 * Configuration for the universal location input (always combined mode)
 */
export const getComponentConfig = (): ComponentConfig => {
  return {
    searchTypes: ['address', 'establishment', 'geocode'],
    placeholder: 'Search for an address or venue...',
    description: 'Search for any address or business location',
    showVenueName: true
  };
};

/**
 * Parse Google Places API result into standardized location data
 * Handles both legacy and new API formats
 */
export const parseGooglePlaceData = (
  place: PlaceData
): Partial<UniversalLocationData> => {
  universalLocationLogger.debug('🔍 RAW PLACE DATA:', place);
  universalLocationLogger.debug('🔍 PLACE KEYS:', Object.keys(place));
  
  let addressComponents: any[] = [];
  let formattedAddress = '';
  let venueName = '';
  
  // Handle both legacy and new API formats
  if (place.addressComponents) {
    // New Places API (New) format
    addressComponents = place.addressComponents;
    formattedAddress = place.formattedAddress || '';
    venueName = place.displayName || '';
    universalLocationLogger.debug('📍 Processing Places API (New) place');
  } else if (place.address_components) {
    // Legacy Places API format
    addressComponents = place.address_components;
    formattedAddress = place.formatted_address || '';
    venueName = place.name || '';
    universalLocationLogger.debug('📍 Processing legacy place');
  } else {
    universalLocationLogger.warn('⚠️ No address components found in place object');
    universalLocationLogger.warn('⚠️ Available place keys:', Object.keys(place));
    return {};
  }
  
  // Map address components to our standardized format
  const components: AddressComponents = {};
  
  universalLocationLogger.debug('🗺️ Raw address components:', addressComponents);
  universalLocationLogger.debug(`🔍 COMPONENTS COUNT: ${addressComponents.length} components found`);
  
  addressComponents.forEach((component: any, index: number) => {
    const types = component.types || [];
    const shortName = component.shortText || component.short_name || '';
    const longName = component.text || component.long_name || '';
    
    // Use non-empty value (prefer longName, fallback to shortName)
    const displayName = longName || shortName;
    const shortValue = shortName;
    
    universalLocationLogger.debug(`📍 Component ${index}: "${longName}" (${shortName}) - Types: [${types.join(', ')}] → Using: "${displayName}"`);
    
    // Check all types, not just the first one (Google Maps components have multiple types)
    types.forEach((type: string) => {
      switch (type) {
        case 'street_number':
          if (!components.street_number) components.street_number = shortValue;
          break;
        case 'route':
          if (!components.route) components.route = displayName; // Use full name for street name
          break;
        case 'locality':
          if (!components.locality) {
            components.locality = displayName; // Use full city name
            universalLocationLogger.debug(`✅ City (locality): ${displayName}`);
          }
          break;
        case 'administrative_area_level_1':
          if (!components.administrative_area_level_1) {
            components.administrative_area_level_1 = shortValue; // Use short name for state code
            universalLocationLogger.debug(`✅ State (admin_level_1): ${shortValue}`);
          }
          break;
        case 'postal_code':
          if (!components.postal_code) components.postal_code = shortValue;
          break;
        case 'country':
          if (!components.country) components.country = shortValue;
          break;
        // Additional city type variations that Google might return
        case 'sublocality':
        case 'sublocality_level_1':
        case 'neighborhood':
          if (!components.locality) {
            components.locality = displayName; // Use full name for city variations
            universalLocationLogger.debug(`✅ City (${type}): ${displayName}`);
          }
          break;
        // County level - don't use for state (would give "Fairfax County" instead of "VA")
        case 'administrative_area_level_2':
          universalLocationLogger.debug(`⚠️ Skipping county (admin_level_2): ${displayName} - need state (admin_level_1) instead`);
          // Don't set state from county - we want proper state codes only
          break;
      }
    });
  });
  
  // Construct street address from components
  const streetAddress = components.street_number && components.route 
    ? `${components.street_number} ${components.route}`
    : formattedAddress.split(',')[0] || ''; // Fallback to first part of formatted address
  
  universalLocationLogger.debug('🏗️ Extracted components:', components);
  
  // Build location data
  const locationData: Partial<UniversalLocationData> = {
    streetAddress,
    city: components.locality || '',
    state: components.administrative_area_level_1 || '',
    zipCode: components.postal_code || ''
  };
  
  // Debug the city extraction
  universalLocationLogger.debug(`🔍 City data - components.locality: "${components.locality}", locationData.city: "${locationData.city}"`);
  
  // Always include venue name (combined mode behavior)
  if (venueName) {
    locationData.venueName = venueName;
  }
  
  universalLocationLogger.info('✅ Location data parsed:', locationData);
  return locationData;
};

/**
 * Validate location data requirements
 */
export const validateLocationData = (
  data: Partial<UniversalLocationData>,
  required = false
): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  if (!required && !data.streetAddress && !data.city && !data.state) {
    // If not required and completely empty, don't validate
    return errors;
  }
  
  // Street address is always required if any address data is provided
  if (!data.streetAddress?.trim()) {
    errors.streetAddress = 'Street address is required';
  }
  
  // City is required for complete addresses
  if (!data.city?.trim()) {
    errors.city = 'City is required';
  }
  
  // State is required for US addresses
  if (!data.state?.trim()) {
    errors.state = 'State is required';
  }
  
  // ZIP code validation (if provided)
  if (data.zipCode && !isValidZipCode(data.zipCode)) {
    errors.zipCode = 'Please enter a valid ZIP code';
  }
  
  return errors;
};

/**
 * Validate US ZIP code format
 */
export const isValidZipCode = (zipCode: string): boolean => {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zipCode.trim());
};

/**
 * Format location data for display
 */
export const formatLocationDisplay = (
  data: UniversalLocationData
): string => {
  universalLocationLogger.debug(`🔍 Display data - city: "${data.city}"`, data);
  const parts: string[] = [];
  
  // Always add venue name if available (combined mode behavior)
  if (data.venueName) {
    parts.push(data.venueName);
  }
  
  // Add street address
  if (data.streetAddress) {
    parts.push(data.streetAddress);
  }
  
  // Add address line 2 if present
  if (data.addressLine2) {
    parts.push(data.addressLine2);
  }
  
  // Add city, state, zip
  const cityStateZip = [
    data.city,
    data.state,
    data.zipCode
  ].filter(Boolean).join(', ');
  
  universalLocationLogger.debug(`🔍 City-state-zip: "${cityStateZip}"`);
  
  if (cityStateZip) {
    parts.push(cityStateZip);
  }
  
  return parts.join(', ');
};

/**
 * Check if location data is complete enough for the given mode
 */
export const isLocationDataComplete = (
  data: Partial<UniversalLocationData>
): boolean => {
  // Basic address requirements
  const hasBasicAddress = !!(
    data.streetAddress?.trim() &&
    data.city?.trim() &&
    data.state?.trim()
  );
  
  // Venue name alone might be sufficient for some use cases (combined mode behavior)
  if (data.venueName?.trim() && !hasBasicAddress) {
    return true;
  }
  
  return hasBasicAddress;
};

/**
 * Merge new location data with existing data, preserving manual edits
 */
export const mergeLocationData = (
  existing: Partial<UniversalLocationData>,
  newData: Partial<UniversalLocationData>,
  preserveFields: (keyof UniversalLocationData)[] = ['addressLine2']
): UniversalLocationData => {
  const merged = { ...existing, ...newData };
  
  // Preserve specific fields that users might have manually edited
  preserveFields.forEach(field => {
    if (existing[field] && !newData[field]) {
      merged[field] = existing[field];
    }
  });
  
  // Ensure required fields have default values
  return {
    streetAddress: merged.streetAddress || '',
    addressLine2: merged.addressLine2 || '',
    city: merged.city || '',
    state: merged.state || '',
    zipCode: merged.zipCode || '',
    ...(merged.venueName && { venueName: merged.venueName })
  };
};